/**
 * The project canvas aspect ratios, defined once.
 *
 * This list used to exist three times (the auto reframe engine, the settings
 * panel, the preview bar) and the three disagreed about which ratios exist and
 * what each one is called. Every surface that offers a canvas size reads from
 * here instead.
 *
 * `ratio` is always the width divided by the height of the pixel dimensions in
 * the same entry, so a caller that crops to `ratio` and a caller that exports
 * at `width x height` cannot end up with two different rectangles.
 */

export type AspectRatioPreset =
  | "16:9"
  | "9:16"
  | "4:5"
  | "4:3"
  | "3:4"
  | "1:1"
  | "21:9"
  | "custom";

export interface AspectRatioConfig {
  readonly id: AspectRatioPreset;
  readonly name: string;
  /**
   * Where this shape is normally published, for the menu sub-label. Absent
   * where there is no established platform to name.
   */
  readonly platforms?: string;
  readonly ratio: number;
  readonly width: number;
  readonly height: number;
}

const definePreset = (
  id: AspectRatioPreset,
  name: string,
  width: number,
  height: number,
  platforms?: string,
): AspectRatioConfig => ({
  id,
  name,
  platforms,
  ratio: width / height,
  width,
  height,
});

export const ASPECT_RATIO_PRESETS: Record<
  AspectRatioPreset,
  AspectRatioConfig
> = {
  "16:9": definePreset("16:9", "Landscape", 1920, 1080, "YouTube, Desktop"),
  "9:16": definePreset("9:16", "Portrait", 1080, 1920, "TikTok, Reels, Shorts"),
  "4:5": definePreset("4:5", "Tall", 1080, 1350, "Instagram, Ads"),
  "4:3": definePreset("4:3", "Classic", 1440, 1080, "Presentations"),
  "3:4": definePreset(
    "3:4",
    "Portrait Classic",
    1080,
    1440,
    "Tablets, Classic Portrait",
  ),
  "1:1": definePreset("1:1", "Square", 1080, 1080, "Instagram, LinkedIn"),
  // 2560x1080 is the standard ultrawide canvas and is universally sold as
  // "21:9", but 2560/1080 is 2.370, slightly wider than 21/9 = 2.333. The name
  // is the industry one and the ratio is what the pixels actually are.
  "21:9": definePreset("21:9", "Cinematic", 2560, 1080),
  custom: definePreset("custom", "Custom", 1920, 1080),
};

/** The rows of the aspect ratio menu, in display order. */
export const ASPECT_RATIO_MENU_ORDER: readonly AspectRatioPreset[] = [
  "16:9",
  "9:16",
  "4:5",
  "4:3",
  "3:4",
  "1:1",
];

/**
 * Every ratio a user can pick. 21:9 is not one of the menu rows but stays
 * selectable from the settings panel and stays a valid auto reframe target.
 */
export const SELECTABLE_ASPECT_RATIOS: readonly AspectRatioPreset[] = [
  ...ASPECT_RATIO_MENU_ORDER,
  "21:9",
];

/** How far a canvas may be from a preset ratio and still be called by its name. */
const RATIO_MATCH_TOLERANCE = 0.01;

/**
 * The preset a canvas size belongs to, or null for a size that is not one of
 * them. An exact pixel match wins over a merely close ratio, so 3840x2160 is
 * reported as 16:9 rather than as some other near miss.
 */
export function matchAspectRatioPreset(
  width: number,
  height: number,
): AspectRatioConfig | null {
  if (!(width > 0) || !(height > 0)) return null;

  const target = width / height;
  let closest: AspectRatioConfig | null = null;
  let smallestDelta = Number.POSITIVE_INFINITY;

  for (const id of SELECTABLE_ASPECT_RATIOS) {
    const config = ASPECT_RATIO_PRESETS[id];
    if (config.width === width && config.height === height) return config;
    const delta = Math.abs(config.ratio - target);
    if (delta < smallestDelta) {
      smallestDelta = delta;
      closest = config;
    }
  }

  return smallestDelta <= RATIO_MATCH_TOLERANCE ? closest : null;
}

/** Short label for a canvas size: its preset id, or "Custom". */
export function aspectRatioLabel(width: number, height: number): string {
  return matchAspectRatioPreset(width, height)?.id ?? "Custom";
}

/** The shape of a media item's probed metadata that canvas sizing depends on. */
export interface MediaCanvasSource {
  width?: number;
  height?: number;
  frameRate?: number;
}

/** A canvas size, and the frame rate to go with it. */
export interface CanvasSettingsForMedia {
  width: number;
  height: number;
  frameRate?: number;
}

/**
 * The canvas an imported clip should land in.
 *
 * Importing used to always build a 1920x1080 project, so a phone clip opened
 * pillarboxed inside a landscape frame and the first thing anyone had to do was
 * go and fix the ratio by hand.
 *
 * The dimensions handed in are MediaBunny's displayWidth and displayHeight,
 * which are already rotation corrected, so a clip recorded sideways reports the
 * shape you actually see rather than its stored shape.
 *
 * The size is snapped to a preset when it is close enough to one. That keeps
 * the aspect ratio menu able to highlight a row rather than reading "Custom",
 * and it stops a 4K import from handing the preview a 3840x2160 canvas.
 * Anything that is genuinely not a standard shape is passed through untouched.
 *
 * Returns null when there is nothing to go on: audio only items, and probes
 * that came back empty, both report zero.
 */
export function canvasSettingsForMedia(
  metadata: MediaCanvasSource | undefined | null,
): CanvasSettingsForMedia | null {
  const width = metadata?.width ?? 0;
  const height = metadata?.height ?? 0;
  if (!(width > 0) || !(height > 0)) return null;

  const preset = matchAspectRatioPreset(width, height);
  const size =
    preset && preset.id !== "custom"
      ? { width: preset.width, height: preset.height }
      : { width, height };

  // A real probed rate only. Zero means the probe found nothing, and passing
  // that on would be worse than leaving the project default in place.
  const frameRate = metadata?.frameRate;
  return frameRate && frameRate > 0 ? { ...size, frameRate } : size;
}
